import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { UploadImagesScroll } from './UploadImagesScroll';
import { useUpload } from '@hooks/prescriptions/useUpload';
import { Prescription, User } from '@interfaces';
import {
  UploadImageMessage,
  UploadButtonIcon,
  UploadImageTextError,
  UploadImagesScrollContainer,
  AddImageInput,
  AddImageDiv,
  ErrorMessageContainer,
  UploadImageMessageContainer,
} from '@components/prescriptions/UploadImage.styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CurrentOrderWithPrescriptionsQueryResponse } from '@queries/order/queries/currentOrderWithPrescriptions';
import { usePendingPrescriptions } from '@hooks/usePendingPrescriptions';
import { AlertModal } from '@components/Modal';
import { useAuth } from '@hooks/useAuth';

interface UploadImageProps {
  emptyImagesError: boolean;
  setEmptyImagesError: (boolean) => void;
  pendingPrescriptions: CurrentOrderWithPrescriptionsQueryResponse['pendingPrescriptions'];
  hasInsurance?: boolean;
  confirmText?: boolean;
}

export function UploadImage({
  emptyImagesError,
  setEmptyImagesError,
  pendingPrescriptions,
  confirmText,
  hasInsurance,
}: UploadImageProps) {
  const { appUser: user } = useAuth();
  const { associatedPrescriptions } = usePendingPrescriptions();
  const { loadFile, loadings, onAskForHelp, errorMessage, setErrorMessage } =
    useUpload();

  const [files, setFiles] = useState([]);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const bigInputRef = useRef<HTMLInputElement>();
  const smallInputRef = useRef<HTMLInputElement>();

  const inputClick = useCallback(() => {
    (bigInputRef.current ?? smallInputRef.current).click();
  }, [bigInputRef, smallInputRef]);

  useEffect(() => {
    setFiles(pendingPrescriptions);
  }, [pendingPrescriptions]);

  const loadingImages = Object.values(loadings).filter(Boolean);
  useEffect(() => {
    if (loadingImages?.length === 0) setIsImageLoading(false);
  }, [loadingImages]);

  const prescriptions = [
    ...(pendingPrescriptions ?? []),
    ...((associatedPrescriptions as Pick<User, 'id' | 'createdAt'>[]) ?? []),
  ].filter(Boolean);

  return (
    <>
      <UploadImageMessageContainer>
        <UploadImageMessage>
          {hasInsurance
            ? 'Recuerda subir foto de todos los productos que quieras reembolsar o requieran receta.'
            : confirmText
            ? 'Recuerda subir foto de todos los productos que necesitan recetas.'
            : 'Sube una foto donde se vea la receta completa.'}
        </UploadImageMessage>
      </UploadImageMessageContainer>
      <UploadImagesScrollContainer>
        {associatedPrescriptions?.map((prescription) => (
          <UploadImagesScroll
            prescription={prescription as Prescription}
            key={(prescription as Prescription)?.id}
            allowRemove={false}
          />
        ))}
        {files?.map((prescription) => (
          <UploadImagesScroll
            prescription={prescription as Prescription}
            key={(prescription as Prescription)?.id}
          />
        ))}
        {loadingImages?.map((_, index) => (
          <UploadImagesScroll loading key={index} />
        ))}
        <AddImageDiv onClick={inputClick} error={emptyImagesError}>
          <AddImageInput
            ref={smallInputRef}
            type="file"
            accept="image/*;capture=camera"
            onChange={(event) => {
              void loadFile(event, setEmptyImagesError);
              setIsImageLoading(true);
            }}
          />
          <UploadButtonIcon error={emptyImagesError}>
            <AddAPhotoIcon />
          </UploadButtonIcon>
        </AddImageDiv>
      </UploadImagesScrollContainer>
      <ErrorMessageContainer>
        {emptyImagesError && !isImageLoading && prescriptions.length === 0 && (
          <UploadImageTextError>
            Debes subir una receta para continuar
          </UploadImageTextError>
        )}
      </ErrorMessageContainer>
      {errorMessage && (
        <AlertModal
          open={!!errorMessage}
          setOpen={() => setErrorMessage(null)}
          title={errorMessage?.title}
          content={errorMessage?.description}
          actionsDirection="row"
          actions={[
            {
              text: 'Volver',
              variant: 'outlined',
              onActionClick: () => {
                setErrorMessage(null);
              },
            },
            {
              text: 'Ayuda',
              variant: 'contained',
              onActionClick: () => {
                onAskForHelp(user.id);
              },
            },
          ]}
        />
      )}
    </>
  );
}
